import React from 'react';
import { Search, Calendar, MapPin, Check, ArrowRight, Clock } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Link } from 'react-router-dom';
import SEO, { generateBreadcrumbSchema } from '../components/SEO';
import CollectionAddress from '../components/CollectionAddress';

// How It Works steps data (previously from mockData)
const howItWorksSteps = [
  {
    id: 1,
    icon: 'Search',
    title: 'Choose Your Motorhome',
    description: 'Browse our selection of 4 and 6 berth motorhomes. All can be driven on a standard UK driving licence!'
  },
  {
    id: 2,
    icon: 'Calendar',
    title: 'Check Availability & Book',
    description: 'Use our interactive calendar to check availability and pricing. Book online with our secure system!'
  },
  {
    id: 3,
    icon: 'MapPin',
    title: 'Enjoy Your Adventure',
    description: 'Collect your motorhome from 3pm (FREE) and enjoy unlimited mileage with full support from our family team!'
  }
];

const HowItWorks = () => {
  const icons = {
    Search: Search,
    Calendar: Calendar,
    MapPin: MapPin
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <SEO title="How Motorhome Hire Works | Booking, Collection & What's Included" rawTitle url="/how-it-works"
        structuredData={generateBreadcrumbSchema([{ name: 'How it Works', path: '/how-it-works' }])}
        description="How to hire a motorhome from Millie's in Suffolk: check live availability, book online, collect at 3pm from Wattisham with free parking, and what's included - insurance, unlimited mileage, gas and kitchen kit." />
      {/* Hero Section */}
      <section className="bg-slate-900 text-white py-20">
        <div className="max-w-7xl mx-auto px-4 text-center">
          <h1 className="text-5xl font-bold mb-6">How It Works</h1>
          <p className="text-xl text-slate-300 max-w-3xl mx-auto">
            Hiring a motorhome from Millie's is simple, straightforward, and stress-free. Here's everything you need to know.
          </p>
        </div>
      </section>

      {/* Steps Section */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-slate-900 mb-4">
              Three Simple Steps
            </h2>
            <p className="text-xl text-slate-600">
              From browsing to adventure in just three easy steps
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-12 mb-16">
            {howItWorksSteps.map((step, index) => {
              const Icon = icons[step.icon];
              return (
                <div key={step.id} className="relative">
                  {index < howItWorksSteps.length - 1 && (
                    <div className="hidden md:block absolute top-16 left-1/2 w-full h-1 bg-blue-200 z-0">
                      <ArrowRight className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-1/2 text-blue-400 h-6 w-6" />
                    </div>
                  )}
                  <Card className="relative z-10 h-full hover:shadow-xl transition-all duration-300">
                    <CardContent className="p-8 text-center">
                      <div className="bg-blue-600 text-white rounded-full w-20 h-20 flex items-center justify-center text-3xl font-bold mb-6 mx-auto shadow-lg">
                        {step.id}
                      </div>
                      <Icon className="h-16 w-16 text-blue-600 mx-auto mb-6" />
                      <h3 className="text-2xl font-bold mb-4">{step.title}</h3>
                      <p className="text-slate-600 leading-relaxed">{step.description}</p>
                    </CardContent>
                  </Card>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Pet-Friendly Section */}
      <section className="py-12 bg-gradient-to-r from-amber-50 to-orange-50">
        <div className="max-w-4xl mx-auto px-4">
          <Card className="border-2 border-amber-200 bg-white">
            <CardContent className="p-8">
              <div className="flex items-center justify-center gap-3 mb-6">
                <div className="bg-amber-100 rounded-full p-3">
                  <svg className="h-8 w-8 text-amber-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
                  </svg>
                </div>
                <h2 className="text-3xl font-bold text-slate-900">Pets Welcome!</h2>
              </div>
              <p className="text-center text-slate-600 mb-6">
                We love well-behaved furry family members! Bring your pets along on your motorhome adventure.
              </p>
              <div className="grid md:grid-cols-2 gap-6 mb-6">
                <div className="bg-amber-50 rounded-xl p-6 text-center border border-amber-200">
                  <p className="text-sm text-slate-600 mb-2">1 Pet</p>
                  <p className="text-4xl font-bold text-amber-600 mb-2">£20</p>
                  <p className="text-slate-500 text-sm">per hire</p>
                </div>
                <div className="bg-amber-50 rounded-xl p-6 text-center border border-amber-200">
                  <p className="text-sm text-slate-600 mb-2">2 Pets</p>
                  <p className="text-4xl font-bold text-amber-600 mb-2">£40</p>
                  <p className="text-slate-500 text-sm">per hire (max 2)</p>
                </div>
              </div>
              <div className="bg-amber-100 rounded-lg p-4 text-center">
                <p className="text-slate-700">
                  Select <strong>1 or 2 pets</strong> via the <strong>extras screen</strong> during your booking
                </p>
              </div>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* Collection & Drop-off Times Section */}
      <section className="py-12 bg-gradient-to-r from-green-50 to-blue-50">
        <div className="max-w-4xl mx-auto px-4">
          <Card className="border-2 border-green-200 bg-white">
            <CardContent className="p-8">
              <div className="flex items-center justify-center gap-2 mb-6">
                <Clock className="h-7 w-7 text-green-600" />
                <h2 className="text-3xl font-bold text-slate-900">Standard Collection & Drop-off Times</h2>
              </div>
              <div className="grid md:grid-cols-2 gap-6 mb-6">
                <div className="bg-green-50 rounded-xl p-6 text-center border border-green-200">
                  <p className="text-sm text-slate-600 mb-2">FREE Collection Time</p>
                  <p className="text-5xl font-bold text-green-600 mb-2">3:00 PM</p>
                  <p className="text-slate-500">On your hire start date</p>
                </div>
                <div className="bg-amber-50 rounded-xl p-6 text-center border border-amber-200">
                  <p className="text-sm text-slate-600 mb-2">FREE Drop-off Time</p>
                  <p className="text-5xl font-bold text-amber-600 mb-2">11:00 AM</p>
                  <p className="text-slate-500">On your hire end date</p>
                </div>
              </div>
              <div className="bg-purple-50 rounded-lg p-4 text-center border border-purple-200 mb-4">
                <p className="font-semibold text-purple-700 mb-1">Need Different Times?</p>
                <p className="text-slate-600">
                  Collection and drop-off times can be extended at an additional cost. Simply select your preferred times via the <strong>extras screen</strong> during the booking process.
                </p>
              </div>
              <CollectionAddress />
            </CardContent>
          </Card>
        </div>
      </section>

      {/* What's Included Section */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-slate-900 mb-4">What's Included</h2>
            <p className="text-xl text-slate-600">Everything you need for a perfect motorhome adventure</p>
          </div>

          <div className="grid md:grid-cols-2 gap-12">
            <Card className="hover:shadow-lg transition-shadow">
              <CardContent className="p-8">
                <h3 className="text-2xl font-bold text-slate-900 mb-6">Standard Equipment</h3>
                <ul className="space-y-3">
                  {[
                    'Fully equipped kitchen with cookware and utensils',
                    'Crockery, cutlery, and glassware',
                    'Pots, pans, and cooking equipment',
                    'Toilet chemicals and cleaning supplies',
                    'Full tank of diesel & AdBlue (must be returned full)',
                    'Gas bottle (1x 6kg)',
                    'Fresh water tank filled',
                    'Comprehensive insurance cover'
                  ].map((item, idx) => (
                    <li key={idx} className="flex items-start gap-3">
                      <Check className="h-5 w-5 text-green-600 flex-shrink-0 mt-0.5" />
                      <span className="text-slate-700">{item}</span>
                    </li>
                  ))}
                </ul>
                <div className="mt-6 p-4 bg-amber-50 border border-amber-200 rounded-lg">
                  <h4 className="font-semibold text-amber-800 mb-2">Optional Extras (Not Included)</h4>
                  <ul className="space-y-2 text-sm">
                    <li className="flex items-start gap-2">
                      <span className="text-amber-600 font-bold">+</span>
                      <span className="text-slate-700">Bedding set: £30 per set (quilt, pillow, towel)</span>
                    </li>
                  </ul>
                </div>
              </CardContent>
            </Card>

            <Card className="hover:shadow-lg transition-shadow">
              <CardContent className="p-8">
                <h3 className="text-2xl font-bold text-slate-900 mb-6">Additional Benefits</h3>
                <ul className="space-y-3">
                  {[
                    'Unlimited mileage - explore as much as you want',
                    '24/7 breakdown assistance',
                    'Comprehensive handover and demonstration',
                    'European travel permitted',
                    'FREE secure car parking - leave your car with us while on hire',
                    'Full customer support throughout hire',
                    'No hidden costs or extra fees',
                    '10% discount for emergency services/armed forces',
                    'Recommended by Camping & Caravanning Club',
                    'Family-run business with personal service'
                  ].map((item, idx) => (
                    <li key={idx} className="flex items-start gap-3">
                      <Check className="h-5 w-5 text-blue-600 flex-shrink-0 mt-0.5" />
                      <span className="text-slate-700">{item}</span>
                    </li>
                  ))}
                </ul>
                
                {/* Car Parking Highlight */}
                <div className="mt-6 p-4 bg-green-50 border border-green-200 rounded-lg">
                  <div className="flex items-center gap-3">
                    <div className="bg-green-100 rounded-full p-2">
                      <svg className="h-6 w-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 8h14M5 8a2 2 0 110-4h14a2 2 0 110 4M5 8v10a2 2 0 002 2h10a2 2 0 002-2V8m-9 4h4" />
                      </svg>
                    </div>
                    <div>
                      <h4 className="font-semibold text-green-800">Free Car Parking</h4>
                      <p className="text-sm text-green-700">Leave your car safely on our premises for the duration of your hire - completely free!</p>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Requirements Section */}
      <section className="py-16 bg-slate-50">
        <div className="max-w-4xl mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-slate-900 mb-4">What You'll Need</h2>
            <p className="text-xl text-slate-600">Requirements for hiring a motorhome</p>
          </div>

          <Card>
            <CardContent className="p-8">
              <div className="space-y-6">
                <div>
                  <h3 className="text-xl font-semibold text-slate-900 mb-3">Driver Requirements</h3>
                  <ul className="space-y-2 ml-4">
                    <li className="flex items-start gap-3">
                      <span className="text-blue-600 font-bold">•</span>
                      <span className="text-slate-700">Cat B1 (standard car licence) - valid for all our motorhomes</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <span className="text-blue-600 font-bold">•</span>
                      <span className="text-slate-700">Valid UK driving licence held for minimum 2 years</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <span className="text-blue-600 font-bold">•</span>
                      <span className="text-slate-700">Minimum age 25 years</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <span className="text-blue-600 font-bold">•</span>
                      <span className="text-slate-700">Maximum age 75 years</span>
                    </li>
                  </ul>
                  <div className="mt-4 ml-4 bg-blue-50 border border-blue-200 rounded-lg p-4 text-sm text-slate-700" data-testid="age-referral-note">
                    <span className="font-semibold text-blue-800">Under 25 or over 75?</span> Don't rule yourself out - we can refer you to our insurers for authorisation on a case-by-case basis. Just get in touch before you book.
                  </div>
                </div>

                <div>
                  <h3 className="text-xl font-semibold text-slate-900 mb-3">Documentation</h3>
                  <ul className="space-y-2 ml-4">
                    <li className="flex items-start gap-3">
                      <span className="text-blue-600 font-bold">•</span>
                      <span className="text-slate-700">Photographic ID (passport or driving licence)</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <span className="text-blue-600 font-bold">•</span>
                      <span className="text-slate-700">Proof of address (utility bill or bank statement within last 3 months)</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <span className="text-blue-600 font-bold">•</span>
                      <span className="text-slate-700">DVLA check code (for driving licence verification)</span>
                    </li>
                  </ul>
                </div>

                <div>
                  <h3 className="text-xl font-semibold text-slate-900 mb-3">Deposit & Payment</h3>
                  <ul className="space-y-2 ml-4">
                    <li className="flex items-start gap-3">
                      <span className="text-blue-600 font-bold">•</span>
                      <span className="text-slate-700">Refundable security deposit of £1,000 (any damages deducted) - returned within 7 days after hire</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <span className="text-blue-600 font-bold">•</span>
                      <span className="text-slate-700">Insurance deposit can be paid by either credit or debit card</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <span className="text-blue-600 font-bold">•</span>
                      <span className="text-slate-700">Full payment required 8 weeks before collection</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <span className="text-blue-600 font-bold">•</span>
                      <span className="text-slate-700">We accept bank transfer, credit and debit cards</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <span className="text-yellow-600 font-bold">★</span>
                      <span className="text-slate-700"><strong>Consider motorhome hire excess insurance cover</strong> to protect you from any expensive bills</span>
                    </li>
                  </ul>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* Camping & Caravanning Club Section */}
      <section className="py-16 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4">
          <Card className="bg-white border-red-200">
            <CardContent className="p-8">
              <div className="grid md:grid-cols-2 gap-8 items-center">
                <div>
                  <Badge className="mb-4 bg-red-600 text-white">
                    OFFICIAL RECOMMENDED SUPPLIER
                  </Badge>
                  <h3 className="text-2xl font-bold text-slate-900 mb-4">
                    Camping & Caravanning Club Benefits
                  </h3>
                  <p className="text-slate-700 mb-6">
                    As an approved supplier by The Camping and Caravanning Club, hiring from us gives you access to exclusive discounts at participating club sites throughout the UK.
                  </p>
                  <ul className="space-y-3">
                    <li className="flex items-start gap-3">
                      <Check className="h-5 w-5 text-green-600 flex-shrink-0 mt-0.5" />
                      <span className="text-slate-700">Discounted rates at hundreds of club sites across the UK</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <Check className="h-5 w-5 text-green-600 flex-shrink-0 mt-0.5" />
                      <span className="text-slate-700">Access to certificated locations in beautiful settings</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <Check className="h-5 w-5 text-green-600 flex-shrink-0 mt-0.5" />
                      <span className="text-slate-700">Quality assured by The Friendly Club standards</span>
                    </li>
                  </ul>
                </div>
                <div className="flex justify-center">
                  <img 
                    src="https://customer-assets.emergentagent.com/job_better-website-5/artifacts/sseihcg6_Screenshot%202026-01-11%20103414.png"
                    alt="The Camping and Caravanning Club"
                    className="max-w-sm w-full"
                  />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-blue-600 text-white">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <h2 className="text-4xl font-bold mb-6">Ready to Book Your Adventure?</h2>
          <p className="text-xl mb-8 text-blue-100">
            Browse our fleet and find the perfect motorhome for your trip
          </p>
          <div className="flex flex-wrap gap-4 justify-center">
            <Button asChild size="lg" className="bg-white text-blue-600 hover:bg-gray-100">
              <Link to="/motorhomes">View Our Fleet</Link>
            </Button>
            <Button asChild size="lg" variant="outline" className="border-white text-white hover:bg-white hover:text-blue-600">
              <a href="mailto:milliesmotorhomehire@gmail.com">Contact Us</a>
            </Button>
            <Button asChild size="lg" variant="outline" className="border-white text-white hover:bg-white hover:text-blue-600">
              <Link to="/terms-and-conditions">
                Terms & Conditions
              </Link>
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default HowItWorks;
